import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Bot, Check, ChevronDown } from 'lucide-react';
import { NavArrowDown } from 'iconoir-react';
import { useTranslation } from 'react-i18next';
import { BUDDY_CONFIG } from 'config/buddy/config';
import { BuddyFace } from 'config/buddy/utils';
import { Button } from 'components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from 'components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { Skeleton } from 'components/ui/skeleton';
import { useTheme } from 'components/core/theme-provider';
import { useConfig } from 'hooks/use-config';
import { useDebounce } from 'hooks/use-debounce';
import { useGetAgentById, useGetAgents } from 'features/chat/hooks/use-agents';
import type { AgentSummary } from 'features/chat/agent-chat-module/types/agent.service.type';
import blocksIcon from 'assets/images/blocks.svg';
import { SIDEBAR_INNER_SCROLL_CLASS } from 'constant/chat-layout';
import { cn } from 'lib/utils';

const AGENTS_LIMIT = 30;
const SEARCH_DEBOUNCE_MS = 300;

interface AgentSelectorProps {
  selectedAgentId: string | null;
  onSelect: (agentId: string | null) => void;
  isDisabled?: boolean;
  /** `tab`: trigger style for the tab above the chat input (Figma 8988:201326). */
  variant?: 'default' | 'tab';
}

export const AgentLogo = ({
  agent,
  className,
}: {
  agent: Pick<AgentSummary, 'logo_url' | 'created_by' | 'platform_managed'>;
  className?: string;
}) => {
  // Same rule as the sidebar: catalog and platform-managed agents use the Blocks mark, not their logo.
  const showBlocksIcon = agent.created_by === 'catalog' || agent.platform_managed === true;
  if (showBlocksIcon) {
    return (
      <img
        src={blocksIcon}
        alt=""
        data-testid="blocks-agent-icon"
        className={cn('h-4 w-4 shrink-0 object-contain', className)}
      />
    );
  }
  if (agent.logo_url) {
    return (
      <img
        src={agent.logo_url}
        alt=""
        className={cn('h-4 w-4 shrink-0 rounded-full object-cover', className)}
      />
    );
  }
  return (
    <Bot
      data-testid="agent-fallback-icon"
      className={cn('h-4 w-4 shrink-0', className)}
      strokeWidth={1.5}
    />
  );
};

export const AgentSelector = ({
  selectedAgentId,
  onSelect,
  isDisabled,
  variant = 'default',
}: AgentSelectorProps) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_MS);
  const { theme } = useTheme();
  const { t } = useTranslation();
  const { configurationSettings } = useConfig();

  const projectKey = configurationSettings?.blocksKey || '';
  const trimmedSearch = debouncedSearch.trim();

  const { data: agentsData, isLoading, isFetching } = useGetAgents(
    {
      limit: AGENTS_LIMIT,
      offset: 0,
      project_key: projectKey,
      show_hidden_agent: false,
      ...(trimmedSearch ? { name: trimmedSearch } : {}),
    },
    { enabled: !!projectKey, scope: 'agent-selector' },
  );

  const agents = useMemo(
    () => (agentsData?.agents ?? []).filter((a) => !a.is_archived && !a.is_disabled),
    [agentsData?.agents],
  );

  const selectedFromList = useMemo(
    () => agents.find((a) => a.id === selectedAgentId) ?? null,
    [agents, selectedAgentId],
  );

  const { data: selectedAgentData } = useGetAgentById(
    { id: selectedAgentId ?? '', projectKey },
    { enabled: !!selectedAgentId && !!projectKey && !selectedFromList },
  );

  const selectedAgent = useMemo(() => {
    if (selectedFromList) return selectedFromList;
    const agent = selectedAgentData?.agent;
    if (!agent || agent.id !== selectedAgentId) return null;
    return agent;
  }, [selectedFromList, selectedAgentData?.agent, selectedAgentId]);

  const noAgentLabel = t('NO_AGENT', { defaultValue: 'No agent' });
  const isSearching = isFetching || search.trim() !== trimmedSearch;

  useEffect(() => {
    if (!open) setSearch('');
  }, [open]);

  if (!isLoading && !trimmedSearch && agents.length === 0 && !selectedAgentId) return null;

  const isTab = variant === 'tab';
  // The tab shows Buddy's face for Buddy and for "no agent" (the default chat),
  // instead of an uploaded logo (Figma 8988:200547).
  const isBuddy = !selectedAgentId || selectedAgentId === BUDDY_CONFIG.agentId;

  let triggerLogo: ReactNode = null;
  if (isTab && isBuddy) {
    triggerLogo = (
      // Button forces descendant svgs to size-4; let the face fill its 24px box.
      <span className="inline-flex size-6 shrink-0 [&_svg]:!size-full">
        <BuddyFace haloId="klaxHaloAgentSelector" />
      </span>
    );
  } else if (selectedAgent) {
    triggerLogo = <AgentLogo agent={selectedAgent} className={isTab ? 'h-6 w-6' : undefined} />;
  }
  const textColor = theme === 'dark' ? 'text-true-white' : 'text-grey-900';

  const handleSelect = (agentId: string | null) => {
    onSelect(agentId);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <PopoverTrigger asChild disabled={isDisabled}>
        <Button
          variant="ghost"
          disabled={isDisabled}
          role="combobox"
          aria-expanded={open}
          className={cn(
            'max-w-36 sm:max-w-40 md:max-w-56 min-w-0 justify-between gap-1 transition-colors duration-200 h-auto px-0 py-1 focus-visible:ring-0 focus-visible:ring-offset-0 focus:outline-none hover:bg-transparent',
            isTab && 'gap-2',
            textColor,
            isDisabled && 'opacity-50 cursor-not-allowed',
          )}
        >
          {triggerLogo}
          <span
            className={cn(
              'overflow-hidden text-ellipsis whitespace-nowrap min-w-0',
              isTab
                ? 'text-sm font-medium leading-body'
                : 'text-sm font-normal leading-5',
              textColor,
            )}
            title={selectedAgent?.name}
          >
            {selectedAgent?.name ?? noAgentLabel}
          </span>
          {isTab ? (
            <NavArrowDown
              width={16}
              height={16}
              strokeWidth={1.75}
              className={cn('shrink-0 transition-transform duration-200', open && 'rotate-180')}
            />
          ) : (
            <ChevronDown className={cn('sm:h-4 sm:w-4 h-3 w-3 shrink-0', textColor)} />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        collisionPadding={12}
        onOpenAutoFocus={(event) => {
          if (window.matchMedia?.('(max-width: 639px)')?.matches) {
            event.preventDefault();
          }
        }}
        className="flex max-h-[min(18rem,var(--radix-popover-content-available-height))] flex-col overflow-hidden"
      >
        <Command shouldFilter={false} className="max-h-full overflow-hidden">
          <CommandInput
            placeholder={t('SEARCH_AGENTS', { defaultValue: 'Search agents...' })}
            value={search}
            onValueChange={setSearch}
          />
          <CommandList className={cn('min-h-0 max-h-72 pt-1', SIDEBAR_INNER_SCROLL_CLASS)}>
            {isSearching ? (
              <div className="space-y-1 p-1">
                {Array.from({ length: 8 }).map((_, index) => (
                  <div key={index} className="flex items-center gap-2 px-2 py-1.5">
                    <Skeleton className="h-4 w-4 shrink-0 rounded-full" />
                    <Skeleton className="h-4 flex-1" />
                  </div>
                ))}
              </div>
            ) : (
              <>
                <CommandEmpty>{t('NO_RESULTS_FOUND')}</CommandEmpty>
                <CommandGroup>
                  <CommandItem
                    value={noAgentLabel}
                    onSelect={() => handleSelect(null)}
                    className="gap-2"
                  >
                    <span
                      data-testid="no-agent-buddy-face"
                      className="inline-flex size-6 shrink-0 [&_svg]:!size-full"
                    >
                      <BuddyFace haloId="klaxHaloAgentSelectorItem" />
                    </span>
                    <span className="flex-1 truncate">{noAgentLabel}</span>
                    {!selectedAgentId && <Check className="!size-4 shrink-0" />}
                  </CommandItem>
                  {agents.map((agent) => (
                    <CommandItem
                      key={agent.id}
                      value={agent.name}
                      onSelect={() => handleSelect(agent.id)}
                      className="gap-2"
                    >
                      <AgentLogo agent={agent} className="!size-6" />
                      <span className="flex-1 truncate" title={agent.name}>
                        {agent.name}
                      </span>
                      {selectedAgentId === agent.id && <Check className="!size-4 shrink-0" />}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
